import {
	Button,
	Card,
	Heading,
	IconButton,
	LuIcon,
	Menu,
	Separator,
	stopPropagation,
	Text,
	toast,
} from "@overmap-ai/blocks"
import { OrganizationAccessLevel, type ProcedureStep, type Stored, ERROR_MSG_UNEXPECTED } from "@overmap-ai/common"
import { APIError, selectProcedureStepsOfProcedureType, selectProcedureTypeById } from "@overmap-ai/core"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { cx } from "class-variance-authority"
import { MoveUp, MoveDown } from "lucide-react"
import { memo, useCallback, useMemo } from "react"

import { ColorButton } from "../../components/Buttons/ColorButton"
import { ViewContainer } from "../../components/Containers/ViewContainer"
import { CreateProcedureStepDialog } from "../../components/Dialogs/CreateProcedureStepDialog"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { interactiveCardCva } from "../../cva"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import { getPriorityStageColor } from "../../utils/colors"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/procedure-types/$procedureTypeId/steps/")

export const ProcedureTypeSettingsStepsView = memo(() => {
	const { procedureTypeId } = routeApi.useParams()

	const procedureType = useAppSelector(selectProcedureTypeById(procedureTypeId))

	if (!procedureType) {
		throw new Error(`expected procedure type to be defined, got ${procedureType}`)
	}
	const navigate = useNavigate()
	const procedureSteps = useShallowEqaulSelector(selectProcedureStepsOfProcedureType(procedureTypeId))

	const orderedProcedureSteps = useMemo(() => {
		return [...procedureSteps].sort((a, b) => a.order - b.order)
	}, [procedureSteps])

	const handleAddProcedureStep = useCallback(
		(payload: Pick<ProcedureStep, "color" | "name">) => {
			const nextOrder =
				orderedProcedureSteps.length > 0
					? orderedProcedureSteps[orderedProcedureSteps.length - 1]!.order + 1
					: 0

			const [_, promise] = sdk.procedureSteps.add({
				...payload,
				order: nextOrder,
				procedure_type: procedureType.uuid,
			})

			promise.catch((reason) => {
				toast.error({
					title: "Could not create step",
					description: reason instanceof APIError ? reason.message : ERROR_MSG_UNEXPECTED,
				})
			})
		},
		[procedureType.uuid, orderedProcedureSteps],
	)

	const handleClickStep = useCallback(
		(procedureStep: Stored<ProcedureStep>) => {
			void navigate({
				from: "/$organizationId/settings/procedure-types/$procedureTypeId/steps/",
				to: "/$organizationId/settings/procedure-types/$procedureTypeId/steps/$procedureStepId",
				params: (prev) => ({
					...prev,
					procedureStepId: procedureStep.uuid,
				}),
			})
		},
		[navigate],
	)

	const handleMoveStep = useCallback(
		(procedureStep: Stored<ProcedureStep>, type: "up" | "down" | "top" | "bottom") => {
			const sourceIndex = orderedProcedureSteps.findIndex(({ uuid }) => uuid === procedureStep.uuid)

			const getDestinationIndex = () => {
				switch (type) {
					case "up":
						return sourceIndex === 0 ? sourceIndex : sourceIndex - 1
					case "down":
						return sourceIndex === orderedProcedureSteps.length - 1 ? sourceIndex : sourceIndex + 1
					case "top":
						return 0
					case "bottom":
						return orderedProcedureSteps.length - 1
				}
			}

			const destinationIndex = getDestinationIndex()
			const newOrderedProcedureSteps = [...orderedProcedureSteps]
			const [removed] = newOrderedProcedureSteps.splice(sourceIndex, 1)
			newOrderedProcedureSteps.splice(destinationIndex, 0, removed)

			sdk.procedureSteps.bulkUpdate(
				newOrderedProcedureSteps.map((procedureStep, index) => ({ uuid: procedureStep.uuid, order: index })),
			)
		},
		[orderedProcedureSteps],
	)

	const handleMoveStepToTop = useCallback(
		(procedureStep: Stored<ProcedureStep>) => {
			handleMoveStep(procedureStep, "top")
		},
		[handleMoveStep],
	)

	const handleMoveStepUp = useCallback(
		(procedureStep: Stored<ProcedureStep>) => {
			handleMoveStep(procedureStep, "up")
		},
		[handleMoveStep],
	)

	const handleMoveStepDown = useCallback(
		(procedureStep: Stored<ProcedureStep>) => {
			handleMoveStep(procedureStep, "down")
		},
		[handleMoveStep],
	)

	const handleMoveStepToBottom = useCallback(
		(procedureStep: Stored<ProcedureStep>) => {
			handleMoveStep(procedureStep, "bottom")
		},
		[handleMoveStep],
	)

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex size-full flex-col gap-4">
				<div className="flex items-center justify-between gap-2">
					<Heading size="xl">Steps</Heading>

					<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
						<CreateProcedureStepDialog
							onSubmit={handleAddProcedureStep}
							defaultColor={getPriorityStageColor(procedureSteps.length)}
							size="sm"
						>
							<Button size="sm" variant="surface" accentColor="primary">
								<LuIcon icon={modelIconMapping.add} /> Create
							</Button>
						</CreateProcedureStepDialog>
					</OrganizationAccessRequired>
				</div>

				<Separator size="full" />

				{orderedProcedureSteps.length > 0 ? (
					<ol className="flex flex-col gap-4">
						{orderedProcedureSteps.map((procedureStep) => (
							<ProcedureStepItem
								key={procedureStep.uuid}
								procedureStep={procedureStep}
								onClick={handleClickStep}
								onMoveToTop={handleMoveStepToTop}
								onMoveUp={handleMoveStepUp}
								onMoveDown={handleMoveStepDown}
								onMoveToBottom={handleMoveStepToBottom}
							/>
						))}
					</ol>
				) : (
					<Text accentColor="base" size="sm" weight="regular">
						No steps configured.
					</Text>
				)}
			</section>
		</ViewContainer>
	)
})
ProcedureTypeSettingsStepsView.displayName = "ProcedureTypeSettingsStepsView"

interface ProceduereStepItemProps {
	procedureStep: Stored<ProcedureStep>
	onClick: (procedureStep: Stored<ProcedureStep>) => void
	onMoveToTop: (procedureStep: Stored<ProcedureStep>) => void
	onMoveUp: (procedureStep: Stored<ProcedureStep>) => void
	onMoveDown: (procedureStep: Stored<ProcedureStep>) => void
	onMoveToBottom: (procedureStep: Stored<ProcedureStep>) => void
}

const ProcedureStepItem = memo((props: ProceduereStepItemProps) => {
	const { procedureStep, onClick, onMoveToTop, onMoveUp, onMoveDown, onMoveToBottom } = props

	const handeClick = useCallback(() => {
		onClick(procedureStep)
	}, [procedureStep, onClick])

	const handleMoveToTop = useCallback(() => {
		onMoveToTop(procedureStep)
	}, [procedureStep, onMoveToTop])

	const handleMoveUp = useCallback(() => {
		onMoveUp(procedureStep)
	}, [procedureStep, onMoveUp])

	const handleMoveDown = useCallback(() => {
		onMoveDown(procedureStep)
	}, [procedureStep, onMoveDown])

	const handleMoveToBottom = useCallback(() => {
		onMoveToBottom(procedureStep)
	}, [procedureStep, onMoveToBottom])

	return (
		<Card
			className={cx("flex w-full cursor-pointer items-center gap-4", interactiveCardCva())}
			role="listitem"
			onClick={handeClick}
			size="sm"
		>
			<ColorButton size="xs" radius="full" color={procedureStep.color} />

			<div className="min-w-0 grow">
				<Text className="truncate">{procedureStep.name}</Text>
			</div>

			<Menu.Root>
				<Menu.ClickTrigger>
					<IconButton size="sm" accentColor="base" variant="ghost" onClick={stopPropagation}>
						<LuIcon icon={modelIconMapping.more} />
					</IconButton>
				</Menu.ClickTrigger>
				<Menu.Content size="sm">
					<Menu.Item onSelect={handleMoveToTop}>
						<LuIcon icon={MoveUp} />
						Move to top
					</Menu.Item>
					<Menu.Item onSelect={handleMoveUp}>
						<LuIcon icon={MoveUp} />
						Move up
					</Menu.Item>
					<Menu.Item onSelect={handleMoveDown}>
						<LuIcon icon={MoveDown} />
						Move down
					</Menu.Item>
					<Menu.Item onSelect={handleMoveToBottom}>
						<LuIcon icon={MoveDown} />
						Move to bottom
					</Menu.Item>
				</Menu.Content>
			</Menu.Root>
		</Card>
	)
})
ProcedureStepItem.displayName = "ProcedureStepItem"
